import { useSubscription } from '@apollo/client/react';

import { DialogStore } from '@components/Dialog';
import { interactionEvent } from '@features/Remote/Interaction/query';
import { setScroll } from '@features/Remote/Interaction/setScroll';
import {
  handleClick,
  handleFocus,
} from '@features/Remote/Interaction/useInteractions';
import {
  Appointment,
  AppointmentInteractionEventLoading,
} from '@generated/gql/graphql';
import { EventTypes, emitter } from '@services/emitter';

interface UseInteractionEventHandlerParams {
  appointmentId: Nullable<string>;
  hasControl: Nullable<boolean>;
  appointment: Nullable<Appointment>;
  userId: Nullable<string>;
  setRequestLoading: (event: AppointmentInteractionEventLoading) => void;
  dialogs: DialogStore;
  isRsScrollSyncEnabled: boolean;
}

const useInteractionEventHandler = ({
  appointmentId,
  hasControl,
  appointment,
  userId,
  setRequestLoading,
  dialogs,
  isRsScrollSyncEnabled,
}: UseInteractionEventHandlerParams) => {
  useSubscription(interactionEvent, {
    variables: {
      id: appointmentId ?? '',
    },
    skip: !appointmentId || !!hasControl,
    onData: ({ data }) => {
      const event = data.data?.appointmentInteractions;
      if (!event) return;

      if (event.__typename === 'AppointmentInteractionEventLoading') {
        setRequestLoading(event);
      }

      if (event.__typename === 'AppointmentInteractionEventFocus') {
        handleFocus(event.element);
      }

      if (event.__typename === 'AppointmentInteractionEventClick') {
        handleClick(event.element);
      }

      if (event.__typename === 'AppointmentInteractionEventDialog') {
        if (event.variant) {
          dialogs[event.id].setVariant(event.variant);
        }
        dialogs[event.id].setOpen(event.open);
        dialogs[event.id].setIsLoading(event.isLoading);
      }

      // TODO update if/when we create a new event for presentation slide
      // scrolling, as this event now handles both presentation and whole-page
      // scroll synchronisation
      if (event.__typename === 'AppointmentInteractionEventScrolling') {
        if (appointment?.controller?.id !== userId) {
          try {
            const payload = JSON.parse(event.scrollingValue);
            if (!isRsScrollSyncEnabled) return;

            setScroll(payload);
          } catch {
            const parts = event.scrollingValue.split(':');
            if (parts[0] === 'slide') {
              emitter.emit(EventTypes.RemotePresentationSlide, parts[1]);
            }
          }
        }
      }
    },
  });
};

export { useInteractionEventHandler };
